
import { useEffect, useRef } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Plus, Link } from "lucide-react";
import { TaskListWithStats } from "@/hooks/useTaskLists";
import { useNavigate } from "react-router-dom";
import { useModalNavigation } from "@/hooks/useModalNavigation";
import { useAuth } from "@/hooks/useAuth";

import { SwipeableTaskItem } from "@/components/tasks/SwipeableTaskItem";
import { useFamily } from "@/hooks/useFamily";
import { useOptimizedTasks } from "@/hooks/useOptimizedTasks";
import {
  useSortable,
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { DndContext, closestCenter, DragEndEvent, PointerSensor, KeyboardSensor, useSensor, useSensors, useDroppable } from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';

interface TaskListCardProps {
  taskList: TaskListWithStats;
  onCardClick: () => void;
  onQuickAdd: () => void;
  onTaskCompleted?: () => void;
  filteredTasks?: any[];
  isDragDisabled?: boolean;
  isOverDropZone?: boolean;
}

export function TaskListCard({ taskList, onCardClick, onQuickAdd, onTaskCompleted, filteredTasks, isDragDisabled = false, isOverDropZone = false }: TaskListCardProps) {
  const navigate = useNavigate();
  const { family, profile } = useFamily();
  const { completeTask, tasks, updateTasksOrder } = useOptimizedTasks(family?.id);
  const { openModal } = useModalNavigation();
  const { user } = useAuth();
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: taskList.id,
    disabled: isDragDisabled,
  });

  // Make the list a droppable zone for tasks from other lists
  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: `droppable-${taskList.id}`,
    data: { type: 'list', listId: taskList.id }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleCompleteTask = async (taskId: string): Promise<void> => {
    await completeTask(taskId);
  };
  const wasDraggingRef = useRef(false);
  useEffect(() => {
    if (isDragging) {
      wasDraggingRef.current = true;
    } else if (wasDraggingRef.current) {
      const t = setTimeout(() => {
        wasDraggingRef.current = false;
      }, 250);
      return () => clearTimeout(t);
    }
  }, [isDragging]);
  
  // Local DnD sensors for tasks inside this list card
  const taskSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6, delay: 100, tolerance: 5 },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  
  const pendingIds = (tasks || [])
    .filter((t) => t.list_id === taskList.id && t.status === 'pending')
    .map((t) => t.id);

  const handleTaskDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = pendingIds.indexOf(String(active.id));
    const newIndex = pendingIds.indexOf(String(over.id));
    if (oldIndex !== -1 && newIndex !== -1) {
      const newOrder = arrayMove(pendingIds, oldIndex, newIndex);
      updateTasksOrder(newOrder);
    }
  };
  return (
    <div 
      ref={(node) => {
        setNodeRef(node);
        setDroppableRef(node);
      }}
      style={style}
      className={`bg-card border rounded-lg p-4 shadow-sm space-y-4 mb-6 transition-all duration-200 ${
        isDragging ? 'opacity-50 scale-105 rotate-2' : ''
      } ${(isOver || isOverDropZone) ? 'border-primary border-2 bg-primary/5' : 'border-border'}`}
    >
      {/* Title Section - Draggable and Clickable */}
      <div className="flex items-center justify-between">
        <div 
          className={`flex items-center gap-2 flex-1 cursor-pointer hover:opacity-80 transition-opacity ${
            isDragDisabled ? '' : 'cursor-grab active:cursor-grabbing'
          }`}
          onClick={() => {
            if (wasDraggingRef.current || isDragging) return;
            openModal('editTaskList', { id: taskList.id })
          }}
          {...attributes}
          {...listeners}
        >
          <div className="flex flex-col gap-0.5 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-2xl">{taskList.icon}</span>
              <h2 className="text-xl font-semibold text-foreground">{taskList.name}</h2>
              {taskList.event_id && <Link className="h-4 w-4 text-muted-foreground" />}
              <span className="text-muted-foreground">({taskList.pending_count || 0})</span>
            </div>
            {taskList.id === 'virtual-general' && taskList.description && (
              <p className="text-sm text-muted-foreground">{taskList.description}</p>
            )}
          </div>
        </div>
        
        {/* Member avatars - filter out current user's avatar */}
        {(() => {
          // Filter out current user's avatar from the list
          const filteredAvatars = (taskList.member_avatars || []).filter((avatar, index) => {
            // This is a simple approach - in a more robust system, you'd have member IDs
            // For now, we assume the first avatar might be the current user if they're the creator
            return !(profile?.avatar_url === avatar);
          });
          
          return filteredAvatars.length > 0 && (
            <div className="flex -space-x-2">
              {filteredAvatars.slice(0, 3).map((avatar, index) => (
                <Avatar key={index} className="h-6 w-6 border-2 border-background">
                  <AvatarImage src={avatar} alt="Member" />
                  <AvatarFallback className="text-xs">
                    {index === 0 ? 'M' : index === 1 ? 'P' : 'A'}
                  </AvatarFallback>
                </Avatar>
              ))}
              {filteredAvatars.length > 3 && (
                <div className="h-6 w-6 bg-muted rounded-full border-2 border-background flex items-center justify-center">
                  <span className="text-xs text-muted-foreground">
                    +{filteredAvatars.length - 3}
                  </span>
                </div>
              )}
            </div>
          );
        })()}
        
        {/* Add Task Button - Hidden for virtual-general */}
        {taskList.id !== 'virtual-general' && (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              openModal('createTask', { listId: taskList.id });
            }}
            className="h-8 w-8 p-0 hover:bg-muted/20 transition-colors ml-2"
          >
            <Plus className="h-4 w-4 text-muted-foreground hover:text-foreground" />
          </Button>
        )}
      </div>
      
      {/* Tasks Content */}
      <div>
        {(() => {
          const tasksToShow = filteredTasks || (taskList.task_previews || []);
          const totalPendingCount = taskList.pending_count || 0;
          const hiddenTasksCount = filteredTasks ? totalPendingCount - filteredTasks.length : 0;
          return tasksToShow.length > 0 ? (
            <>
              <DndContext
                sensors={taskSensors}
                collisionDetection={closestCenter}
                onDragEnd={handleTaskDragEnd}
                modifiers={[restrictToVerticalAxis, restrictToParentElement]}
              >
                <SortableContext
                  items={tasksToShow.map(t => t.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-3">
                    {tasksToShow.map((task) => (
                       <SwipeableTaskItem
                         key={task.id}
                         task={task}
                         onTaskClick={() => openModal('viewTask', { id: task.id })}
                         onTaskCompleted={onTaskCompleted}
                         canComplete={task.status === 'pending'}
                         completeTask={handleCompleteTask}
                         isDraggable
                         currentUserProfileId={profile?.id}
                         taskCreatedBy={task.created_by}
                       />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
              
              {filteredTasks && hiddenTasksCount > 0 && (
                <div className="text-xs text-muted-foreground pt-1">
                  {hiddenTasksCount} tâche{hiddenTasksCount > 1 ? 's' : ''} masquée{hiddenTasksCount > 1 ? 's' : ''} par la recherche
                </div>
              )}
            </>
          ) : (
            <div className="text-sm text-muted-foreground">
              Aucune tâche en cours
            </div>
          );
        })()}
        
      </div>
    </div>
  );
}